import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { combineLatest, Observable, of, Subject, throwError } from 'rxjs';

import { MessageTypeEnum } from '@app/ngrx-message/constants/messageTypeEnum';
import {
  HideInvoiceAndPayments,
  PayInvoiceUser,
} from '@app/user/constants/user-profile.constants';
import { selectUserProfiles } from '@app/user/store';
import { PopoverController } from '@ionic/angular';
import { Dictionary } from '@ngrx/entity';
import {
  jwtDecode,
  LOCAL_STORAGE_KEYS,
  PanAppState,
  SESSION_STORAGE_KEYS,
} from '@panamax/app-state';
import { MAX_INVOICE_SELECTION } from '@payments/constants/constants';
import { ErrorPopoverComponent } from '@shared/components/header/error-popover/error-popover.component';
import { FEATURES } from '@shared/constants/splitio-features';
import { PopoverHelperService } from '@shared/helpers/popover.helpers';
import { AppService } from '@shared/services/app.service';
import { ClaimTicketService } from '@shared/services/claim-ticket.service';
import { ServiceHandlerService } from '@shared/services/service-handler.service';
import { ToastService } from '@shared/services/toast/toast.service';
import { SplitService } from '@splitsoftware/splitio-angular';
import {
  AccountSummaryStoreService,
  BankStoreService,
  Filter,
  InvoiceFilterIds,
  invoicesSelectors,
  InvoiceStoreService,
  PaymentCustomerStoreService,
  PaymentStoreService,
  RecentCreditStoreService,
  SortDirection,
  SortFilter,
} from '@usf/ngrx-payment';
import { NewADBankAccount } from '@usf/ngrx-payment/lib/models/new-ad-bank-account.model';
import {
  ISRDocumentResponse,
  PaymentTransactionsViewModel,
  SortFields,
  Status,
  TransactionGroupFilter,
} from '@usf/payment-types';
import {
  catchError,
  distinctUntilKeyChanged,
  filter,
  map,
  skipWhile,
  switchMap,
  take,
  takeUntil,
} from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { PaymentsPageViewModel } from '../models/payments-page-view.model';
import {
  getPaymentsPageViewModel,
  getSelectedPaymentCustomers,
} from '../selectors/payments.selector';
import { PaymentsAnalyticsService } from '../tracking/analytics/payments-analytics.service';
import { FileHandlerPaymentsService } from './file-handler-payments.service';

@Injectable({
  providedIn: 'root',
})
export class PaymentsService {
  thirtyDays = 1000 * 60 * 60 * 24 * 30;
  recentTransactionSort: SortFilter[] = [
    { fieldName: SortFields.issueDate, sortValue: SortDirection.DESC },
    { fieldName: SortFields.transaction, sortValue: SortDirection.DESC },
  ];

  constructor(
    private store: Store,
    private paymentCustomerService: PaymentCustomerStoreService,
    private paymentsAnalytics: PaymentsAnalyticsService,
    private toastService: ToastService,
    private panAppState: PanAppState,
    private invoiceStoreService: InvoiceStoreService,
    private recentCreditStoreService: RecentCreditStoreService,
    private accountSummaryStoreService: AccountSummaryStoreService,
    private popoverController: PopoverController,
    private appService: AppService,
    private claimTicketService: ClaimTicketService,
    private popoverHelperService: PopoverHelperService,
    private serviceHandler: ServiceHandlerService,
    private paymentStoreService: PaymentStoreService,
    private bankStoreService: BankStoreService,
    private splitService: SplitService,
    private fileHandlerService: FileHandlerPaymentsService,
  ) {}

  clearPaymentCustomer(): void {
    localStorage.removeItem(LOCAL_STORAGE_KEYS.paymentCustomer);
  }

  clearEligibleADCustomers(): void {
    this.bankStoreService.clearEligibleCustomers();
  }

  getEligibleADCustomers(): void {
    this.bankStoreService.getEligibleADCustomers();
  }

  clearPayments() {
    this.paymentStoreService.clearPayments();
  }

  downloadInvoices(
    fileName: string,
    transactions: PaymentTransactionsViewModel[],
    showOrderNumber: boolean,
  ) {
    this.fileHandlerService.downloadAllInvoices(
      fileName,
      transactions,
      showOrderNumber,
    );
  }

  printInvoices(
    fileName: string,
    transactions: PaymentTransactionsViewModel[],
    fromDate?: Date,
    toDate?: Date,
  ) {
    this.fileHandlerService.printAllInvoices(
      fileName,
      transactions,
      fromDate,
      toDate,
    );
  }

  shareInvoices(
    fileName: string,
    transactions: PaymentTransactionsViewModel[],
    fromDate?: Date,
    toDate?: Date,
  ) {
    this.fileHandlerService.shareInvoices(
      fileName,
      transactions,
      fromDate,
      toDate,
    );
  }

  fetchInvoices(
    pageNum: number,
    pageSize?: number,
    restrictToOpen?: boolean,
    keepSelectedInvoices = false,
  ) {
    this.invoiceStoreService.fetchInvoices(
      pageNum,
      restrictToOpen,
      pageSize,
      keepSelectedInvoices,
    );
  }

  selectInvoice(
    transaction: PaymentTransactionsViewModel,
    translatedIDs: Dictionary<string> = {},
  ) {
    this.invoiceStoreService.selectInvoices([transaction._id], translatedIDs);
  }

  selectInvoices(transactions: PaymentTransactionsViewModel[]) {
    const ids = transactions.map(transaction => transaction._id);
    this.invoiceStoreService.selectInvoices(ids);
  }

  downloadAutopayAchForm(bankAdSetup: NewADBankAccount, signature: string) {
    const {
      customerNumbers,
      bankID,
      autoDeductStartDate,
      routingNumber,
      accountNumber,
      bankName,
    } = bankAdSetup;

    const body = {
      customerNumbers,
      bankID,
      autoDeductStartDate,
      routingNumber,
      accountNumber,
      bankName,
      signature,
    };

    this.serviceHandler
      .post(`${environment.paymentBankApiUrl}/generateACHForm`, body)
      .pipe(take(1))
      .subscribe(
        (docResponse: ISRDocumentResponse) => {
          this.fileHandlerService.downloadAction(
            docResponse,
            false,
            'invoiceDownload.unableToView',
            'AutopaySetupForm',
          );
        },
        _ => {
          this.fileHandlerService.showErrorToast(
            'i18n.payments.paymentPreferences.autopaySetup.achPdfDownloadFailure',
          );
        },
      );
  }

  fetchTranslatedIds(invoiceIDs: string[]): Observable<any> {
    const body = {
      invoiceIDs,
    };
    return this.serviceHandler
      .post(`${environment.paymentApiUrl}/translate`, body)
      .pipe(
        take(1),
        filter(res => !!res),
        catchError(error => {
          console.error('http error', error);
          return throwError(() => new Error('Error translating ids ' + error));
        }),
      );
  }

  selectAllInvoices(
    maxTransactionsSelectedMessage: string,
    fetchArgs?: { restrictToOpen: boolean },
    maxSelection?: number,
  ) {
    if (maxSelection <= 0) {
      this.showToastMessage(maxTransactionsSelectedMessage);
      return;
    }

    maxSelection = maxSelection || MAX_INVOICE_SELECTION;
    const subscription = new Subject<boolean>();
    this.store
      .select(invoicesSelectors.selectInvoices)
      .pipe(
        takeUntil(subscription),
        filter(state => !state.loading),
        distinctUntilKeyChanged('ids'),
      )
      .subscribe(invoiceState => {
        if (invoiceState.ids.length < maxSelection && invoiceState.nextPage) {
          this.fetchInvoices(
            1,
            MAX_INVOICE_SELECTION,
            fetchArgs?.restrictToOpen,
            true,
          );
        } else {
          this.invoiceStoreService.selectMaxInvoices(maxSelection);
          if (invoiceState.nextPage) {
            this.showToastMessage(maxTransactionsSelectedMessage);
          }
          subscription.next(true);
          subscription.complete();
        }
      });
  }

  checkFlagByDivisions(flag: string, divisions: number[]): Observable<boolean> {
    const desiredTreatment = 'on';
    return combineLatest(
      divisions.map(division => {
        if (this.splitService.getSDKClient(`${division}`) === undefined) {
          this.splitService.initClient(`${division}`);
        }

        return this.splitService
          .getClientSDKReady(`${division}`)
          .pipe(map(() => this.splitService.getTreatment(`${division}`, flag)));
      }),
    ).pipe(
      map(treatments =>
        treatments.some(treatment => treatment === desiredTreatment),
      ),
    );
  }

  clearSelectedInvoices() {
    this.invoiceStoreService.clearSelectedInvoices();
  }

  getPaymentsVM$(): Observable<PaymentsPageViewModel> {
    return combineLatest([
      this.store.select(getPaymentsPageViewModel),
      this.isNetworkUser(),
      this.panAppState.feature$([FEATURES.split_global_payments_outage.name]),
      this.panAppState.feature$([FEATURES.split_user_payments_dashboard.name]),
      this.panAppState.feature$([
        FEATURES.split_user_payments_dashboard_override.name,
      ]),
      this.panAppState.feature$([FEATURES.split_user_payments_autopay.name]),
    ]).pipe(
      map(
        ([
          vm,
          isNetworkUser,
          paymentHasOutage,
          isPaymentDashboardOn,
          overrideNetworkUser,
          isAutopayOn,
        ]) => {
          const isDashboardOn =
            isPaymentDashboardOn &&
            (!!isNetworkUser || overrideNetworkUser) &&
            !vm.isSuperUser;
          return {
            ...vm,
            paymentHasOutage,
            isDashboardOn,
            isAutopayOn,
          };
        },
      ),
    );
  }

  getSelectedPaymentCustomers() {
    return this.store.select(getSelectedPaymentCustomers);
  }

  switchPaymentCustomer(customers: number[]): void {
    localStorage.setItem(
      LOCAL_STORAGE_KEYS.paymentCustomer,
      JSON.stringify(customers),
    );
    this.paymentCustomerService.customerUpdated(customers);
    this.getAccountSummaryInvoicesAndCredits(customers);
    this.fetchOpenInvoices(customers);
    this.setInvoiceFilters(customers);
  }

  getAccountSummaryInvoicesAndCredits(customers: number[]) {
    this.accountSummaryStoreService.getAccountSummary(customers);
    this.recentCreditStoreService.fetchRecentCredits(customers);
  }

  setInitialInvoicesSortAndFilter() {
    this.setInvoiceFilters([]);
    this.invoiceStoreService.setInvoiceSort(
      [
        {
          fieldName: SortFields.issueDate,
          sortValue: SortDirection.DESC,
        },
        {
          fieldName: SortFields.transaction,
          sortValue: SortDirection.DESC,
        },
      ],
      false,
    );

    this.getSelectedPaymentCustomers()
      .pipe(
        filter(paymentCustomer => paymentCustomer.customers?.length > 0),
        take(1),
      )
      .subscribe(paymentCustomer => {
        this.fetchOpenInvoices(paymentCustomer.customers);
        this.setInvoiceFilters(paymentCustomer.customers);
      });
  }

  fetchOpenInvoices(customers: number[]) {
    this.invoiceStoreService.fetchOpenInvoices(
      [
        {
          filterName: InvoiceFilterIds.Status,
          value: [Status.OVERDUE, Status.UNPAID, Status.PARTIALLY_PAID],
        },
        {
          filterName: InvoiceFilterIds.Customers,
          value: customers,
        },
      ],
      true,
    );
  }

  setInvoiceFilters(customers: number[]) {
    const thirtyDays = 1000 * 60 * 60 * 24 * 30;
    const now = new Date();
    const todaysDate = this.formatDate(now);
    const thirtyDaysAgo = this.formatDate(new Date(now.valueOf() - thirtyDays));

    let invoiceFilters: Filter[] = [
      {
        filterName: InvoiceFilterIds.FromIssueDate,
        value: thirtyDaysAgo,
      },
      {
        filterName: InvoiceFilterIds.ToIssueDate,
        value: todaysDate,
      },
      {
        filterName: InvoiceFilterIds.TransactionGroup,
        value: [
          TransactionGroupFilter.INVOICE_ALL,
          TransactionGroupFilter.DEBIT,
          TransactionGroupFilter.OTHER,
        ],
      },
    ];

    if (customers.length) {
      invoiceFilters.push({
        filterName: InvoiceFilterIds.Customers,
        value: customers,
      });
    }

    this.invoiceStoreService.setInvoiceFilters(invoiceFilters, false, false);
  }

  setBalancePaymentInvoices(callback: (showError: boolean) => void) {
    this.getSelectedPaymentCustomers()
      .pipe(
        filter(paymentCustomer => paymentCustomer.customers?.length > 0),
        switchMap(paymentCustomer => {
          this.setPayBalanceInitialFiltersAndSort(paymentCustomer.customers);
          return this.store.select(invoicesSelectors.selectInvoices);
        }),
        skipWhile(
          state =>
            (state.ids.length < MAX_INVOICE_SELECTION && state.nextPage) ||
            state.loading,
        ),
        take(1),
      )
      .subscribe(state => {
        this.invoiceStoreService.selectMaxInvoices();
        callback(state.nextPage);
      });
  }

  setPayBalanceInitialFiltersAndSort(customers: number[]) {
    this.invoiceStoreService.setInvoiceFilters(
      [
        {
          filterName: InvoiceFilterIds.Customers,
          value: customers,
        },
        {
          filterName: InvoiceFilterIds.Status,
          value: [Status.OVERDUE, Status.PARTIALLY_PAID, Status.UNPAID],
        },
      ],
      true,
      false,
      MAX_INVOICE_SELECTION,
    );
    this.invoiceStoreService.setInvoiceSort(
      [
        {
          fieldName: SortFields.dueDate,
          sortValue: SortDirection.ASC,
        },
      ],
      true,
      MAX_INVOICE_SELECTION,
    );
  }

  goToLegacyPlatform(isTouch: boolean) {
    if (isTouch) {
      window.open(environment.myAccountUrl, '_blank');
    } else {
      this.navigateWithClaimTicket(environment.paymentsUrl);
    }
  }

  navigateWithClaimTicket(url: string) {
    this.claimTicketService.getClaimTicket()?.subscribe(
      res => {
        if (res?.claimTicketValue) {
          url = url + res?.claimTicketValue;
          this.appService.openUrlInBrowser(url);
        } else {
          this.popoverHelperService.presentPopover(
            this.popoverController,
            ErrorPopoverComponent,
            `Unable to take you to Pay Balance at this time. Please try again later.`,
          );
        }
      },
      () => {
        this.popoverHelperService.presentPopover(
          this.popoverController,
          ErrorPopoverComponent,
          `Unable to take you to Pay Balance at this time. Please try again later.`,
        );
      },
    );
  }

  trackPageLoad(): void {
    this.paymentsAnalytics.trackPageLoad();
  }

  formatDate(date: Date): number {
    return +date.toJSON().replace(/-/g, '').substring(0, 8);
  }

  showToastMessage(
    message: string,
    sticky: boolean = false,
    type: MessageTypeEnum = MessageTypeEnum.error,
  ) {
    this.toastService.presentToast({
      id: '',
      type,
      display: message,
      stack: '',
      watermark: '',
      read: false,
      sticky,
    });
  }

  showPaymentsOutageMessageIfNeeded(
    paymentHasOutage: boolean,
    outageMessage: string,
  ) {
    if (
      paymentHasOutage &&
      !sessionStorage.getItem(
        SESSION_STORAGE_KEYS.paymentsOutageMessageDismissed_LandingPage,
      )
    ) {
      this.showToastMessage(outageMessage, true, MessageTypeEnum.warning);
      sessionStorage.setItem(
        SESSION_STORAGE_KEYS.paymentsOutageMessageDismissed_LandingPage,
        'true',
      );
    }
  }

  getUserPaymentAccess() {
    return combineLatest([
      this.store.select(selectUserProfiles),
      this.panAppState.feature$([FEATURES.split_user_payments_link.name]),
    ]).pipe(
      filter(([userProfiles]) => !!userProfiles && !!userProfiles.length),
      map(([userProfiles, flag]) => {
        const hideInvoiceAndPayments = userProfiles?.find(
          userProf => userProf.key === HideInvoiceAndPayments,
        );
        if (hideInvoiceAndPayments?.value === 'Y') {
          return false;
        }
        return flag;
      }),
    );
  }

  getUserMakePaymentAccess() {
    return combineLatest([
      this.getUserPaymentAccess(),
      this.store.select(selectUserProfiles),
      this.panAppState.feature$([
        FEATURES.split_user_payments_make_a_payment.name,
      ]),
    ]).pipe(
      map(([userPaymentAccess, userProfiles, flag]) => {
        const payInvoiceUser = userProfiles?.find(
          userProf => userProf.key === PayInvoiceUser,
        );
        return userPaymentAccess && payInvoiceUser?.value === 'Y' && flag;
      }),
    );
  }

  private isNetworkUser(): Observable<boolean> {
    return this.panAppState.accessToken$.pipe(
      switchMap(token =>
        of(jwtDecode(token)?.['usf-claims']?.userType === 'corp-ad'),
      ),
    );
  }
}
